<template>
  <div class="mt-4">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <label class="small font-weight-bold text-muted mb-0">Vista previa de la estampa</label>
      <div class="d-flex align-items-center">
        <label class="small text-muted mb-0 mr-1">Zoom</label>
        <b-form-select
          v-model.number="previewZoom"
          size="sm"
          :options="zoomOptions"
          style="width: auto;"
          v-b-tooltip.hover
          :title="zoomTooltip"
        />
        <feather-icon
          icon="InfoIcon"
          size="14"
          class="ml-1 text-muted"
          v-b-tooltip.hover
          :title="zoomTooltip"
        />
        <span class="badge badge-light border text-muted ml-2" title="Ancho x alto que se está usando">
          {{ effectiveAncho }} × {{ effectiveAlto }}
        </span>
      </div>
    </div>
    <div class="contenedor-preview-estampa" :style="previewBoxStyle">
      <img :src="previewUrl" alt="Preview" v-if="previewUrl && !isLoading"/>
      <div v-else-if="isLoading">Cargando...</div>
      <div v-else>No hay estampa disponible</div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import {signConfigStamp} from '@/febos/servicios/api/usuarios.api';
import vueSignature from 'vue-signature';

export default {
  name: 'PreviewStamp',
  components: {vueSignature},
  props: {
    user: {
      type: Object,
      required: true
    },
    prefix: {
      type: String,
      default: 'asignapi'
    },
    customConfigIds: {
      type: Object,
      default: null
    },
    proveedor: {
      type: String,
      default: ''
    },
    tipoFirma: {
      type: String,
      default: 'firmaAvanzada'
    }
  },
  data() {
    return {
      defaultAncho: 150,
      defaultAlto: 50,
      // Escala visual: 1 unidad lógica = previewFactor px de ancho en pantalla.
      // Con factor 1, el ancho en pantalla coincide con el ancho configurado
      // (300 -> 300px), para que el preview sea lo más fiel posible.
      previewFactor: 1,
      // Tope de seguridad para estampas muy anchas / zoom alto; el contenedor
      // además está limitado por max-width: 100%.
      previewMaxWidth: 1200,
      // Zoom visual (solo amplia la vista, no la imagen final).
      previewZoom: 1,
      zoomTooltip: 'Amplía la vista de la estampa. No afecta el tamaño ni la imagen final.',
      zoomOptions: [
        { value: 1, text: '1x' },
        { value: 2, text: '2x' },
        { value: 3, text: '3x' },
        { value: 4, text: '4x' },
        { value: 5, text: '5x' }
      ],
      ancho: '',
      alto: '',
      previewUrl: '',
      isLoading: true
    };
  },
  computed: {
    ...mapGetters('Empresas', ['company']),
    ...mapGetters('Usuario', ['currentUser']),
    configIds() {
      if (this.customConfigIds) return this.customConfigIds;
      const p = this.prefix;
      return {
        tipo: `io.febos.${p}.tipo`,
        plantilla: `io.febos.${p}.plantilla`,
        imagen: `io.febos.${p}.imagen`,
        background: `io.febos.${p}.background`,
        representacion: `io.febos.${p}.representacion`,
        alto: `io.firma.${p}.estampa.al`,
        ancho: `io.firma.${p}.estampa.an`
      };
    },
    proveedorActual() {
      return this.proveedor || this.company.firmaAvanzada || this.company.firmaSimple || '';
    },
    // Dimensiones lógicas efectivas de la estampa (con fallback a los defaults
    // del firmador: 150 ancho x 50 alto).
    effectiveAncho() {
      const w = parseInt(this.ancho, 10);
      return Number.isNaN(w) || w <= 0 ? this.defaultAncho : w;
    },
    effectiveAlto() {
      const h = parseInt(this.alto, 10);
      return Number.isNaN(h) || h <= 0 ? this.defaultAlto : h;
    },
    // Caja de preview con proporción real de la estampa (ancho/alto de la config).
    // El ancho se escala por el zoom visual, limitado por previewMaxWidth y por
    // max-width:100% al ancho disponible.
    previewBoxStyle() {
      const z = parseInt(this.previewZoom, 10);
      const safeZoom = Number.isNaN(z) || z <= 0 ? 1 : z;
      const scaled = this.effectiveAncho * this.previewFactor * safeZoom;
      return {
        width: `${Math.min(scaled, this.previewMaxWidth)}px`,
        maxWidth: '100%',
        aspectRatio: `${this.effectiveAncho} / ${this.effectiveAlto}`
      };
    },
    imageFullUrl() {
      if (!this.config.representacion) return '';
      if (this.config.representacion.startsWith('http')) return this.config.representacion;
      const path = this.config.representacion.startsWith('/') ? this.config.representacion.substring(1) : this.config.representacion;
      return `https://${path}`;
    },
    imagenCargadaUrl() {
      if (!this.config.imagen) return '';
      if (this.config.imagen.startsWith('http')) return this.config.imagen;
      const path = this.config.imagen.startsWith('/') ? this.config.imagen.substring(1) : this.config.imagen;
      return `https://${path}`;
    },
    representationFullUrl() {
      if (this.localRepresentationPreview) return this.localRepresentationPreview;
      return this.imageFullUrl;
    },
    backgroundFullUrl() {
      if (!this.config.background) return '';
      if (this.config.background.startsWith('http')) return this.config.background;
      const path = this.config.background.startsWith('/') ? this.config.background.substring(1) : this.config.background;
      return `https://${path}`;
    },
    previewUserData() {
      return {
        nombre: this.currentUser ? this.currentUser.nombre : 'Nombre Usuario',
        cargo: this.currentUser ? (this.currentUser.cargo || 'Cargo') : 'Cargo',
        iut: this.user ? this.user.iut : '12345678-9',
        correo: this.currentUser ? this.currentUser.correo : 'correo@empresa.cl'
      };
    },
    previewCompanyData() {
      return {
        razonSocial: this.company ? this.company.razonSocial : 'Empresa S.A.'
      };
    }
  },
  methods: {
    async loadCurrentConfigurations() {
      try {
        this.isLoading = true;
        const configuraciones = Object.values(this.configIds);
        // En paralelo: la imagen ya generada (PREVISUALIZAR_FIRMA) y los valores
        // de configuración (OBTENER_CONFIG_ESTAMPA) para conocer ancho/alto.
        const [previewResp, configResp] = await Promise.all([
          signConfigStamp({
            categoria: 'PREVISUALIZAR_FIRMA',
            idUsuarioFirma: this.user.id,
            parametros: { configuraciones },
            proveedor: this.proveedorActual,
            tipoFirma: this.tipoFirma
          }),
          signConfigStamp({
            categoria: 'OBTENER_CONFIG_ESTAMPA',
            idUsuarioFirma: this.user.id,
            parametros: { configuraciones },
            proveedor: this.proveedorActual,
            tipoFirma: this.tipoFirma
          }).catch(() => null)
        ]);

        const previewParams = previewResp.data && previewResp.data.parametros ? previewResp.data.parametros : {};
        this.previewUrl = previewParams['estampa'];

        const configParams = configResp && configResp.data && configResp.data.parametros ? configResp.data.parametros : {};
        if (configParams[this.configIds.alto]) this.alto = configParams[this.configIds.alto];
        if (configParams[this.configIds.ancho]) this.ancho = configParams[this.configIds.ancho];
      } catch (error) {
        // Si falla, usamos los defaults
        console.debug('No se pudieron cargar configuraciones de estampa', error);
      } finally {
        this.isLoading = false;
      }
    }
  },
  async mounted() {
    await this.loadCurrentConfigurations();
  }
};
</script>
<style scoped>
.contenedor-preview-estampa {
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
  /* Centrar la caja (de ancho fijo) dentro del contenedor. */
  margin-left: auto;
  margin-right: auto;
}
img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
}
</style>
